import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isNum, fx, fmtN, rankText, DeltaRank, DeltaPct, ymd, niceMax } from './analytics/format';
import { StarButton, StarDialog } from './analytics/star';

// MEO分析の店舗一覧。形は依頼者の画面案のとおり（立地はデータが無いので無い）。
//
// 全店舗ぶんの行を最初に受け取り、絞り込み・並べ替え・アクション率の分布はこの中で計算する。
// 行の数字と要対応スコアはサーバーで作っている（GbpAnalytics::StoreList）。
// ★（トラブル中・要対応必須などの目印）は区分（と任意の理由）を入れて付け、付けた店は一覧の上に出す。
// 付け外しはその場で保存し（AnalyticsController#star）、画面の行だけ書き換える。

const STATUS = {
  crit: { label: '要対応', color: '#e03131' },
  warn: { label: '注意', color: '#f08c00' },
  good: { label: '良好', color: '#2f9e44' }
};

// 文字の列は昇順、数字の列は「悪いほうが上」になる向きで並べ始める
const SORT_START = {
  score: -1, name: 1, industry: 1, pref: 1, staff: 1, kw1: 1, avg: 1, uu: -1, rate: -1, since: -1, renewDays: 1
};

const RENEW_OPTIONS = [['', 'すべて'], ['30', '30日以内'], ['60', '60日以内'], ['90', '90日以内']];

const uniqSorted = (values) => Array.from(new Set(values.filter(Boolean))).sort((a, b) => a.localeCompare(b, 'ja'));

export default function StoreList(props) {
  const { days, scoreLabels, outOfRank, starKinds, urls } = props;
  const [rows, setRows] = useState(props.rows);

  const [q, setQ] = useState('');
  const [industry, setIndustry] = useState('');
  const [company, setCompany] = useState('');
  const [pref, setPref] = useState('');
  const [staff, setStaff] = useState('');
  const [renew, setRenew] = useState('');
  const [critOnly, setCritOnly] = useState(false);
  const [starOnly, setStarOnly] = useState(false);
  const [starTop, setStarTop] = useState(true);
  const [starOf, setStarOf] = useState(null);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!toast) return undefined;
    const id = setTimeout(() => setToast(''), 2500);
    return () => clearTimeout(id);
  }, [toast]);

  const onStarSaved = (id, star) => {
    setRows((list) => list.map((r) => (r.id === id ? { ...r, star } : r)));
    setStarOf(null);
    setToast(star ? '★を保存しました' : '★を外しました');
  };
  const [sort, setSort] = useState({ key: 'score', dir: -1 });

  const choices = useMemo(() => {
    const companies = {};
    rows.forEach((r) => {
      if (!r.companyId) return;
      const c = companies[r.companyId] || (companies[r.companyId] = { id: r.companyId, name: r.company || '', n: 0 });
      c.n += 1;
    });
    return {
      industries: uniqSorted(rows.map((r) => r.industry)),
      prefs: uniqSorted(rows.map((r) => r.pref)),
      staffs: uniqSorted(rows.map((r) => r.staff)),
      companies: Object.values(companies).sort((a, b) => b.n - a.n || a.name.localeCompare(b.name, 'ja'))
    };
  }, [rows]);

  const filtered = useMemo(() => {
    const word = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (word && ![r.code, r.name, r.company].some((v) => v && v.toLowerCase().includes(word))) return false;
      if (industry && r.industry !== industry) return false;
      if (company && String(r.companyId) !== company) return false;
      if (pref && r.pref !== pref) return false;
      if (staff && r.staff !== staff) return false;
      if (renew && !(isNum(r.renewDays) && r.renewDays >= 0 && r.renewDays <= Number(renew))) return false;
      if (critOnly && r.status !== 'crit') return false;
      if (starOnly && !r.star) return false;
      return true;
    });
  }, [rows, q, industry, company, pref, staff, renew, critOnly, starOnly]);

  // 値の無い行は、向きにかかわらずいつも下に置く。「★を上に表示」のときは★の店を先に
  const sorted = useMemo(() => {
    const { key, dir } = sort;
    return filtered.slice().sort((a, b) => {
      if (starTop && !a.star !== !b.star) return a.star ? -1 : 1;
      const x = a[key]; const y = b[key];
      const xe = x === null || x === undefined || x === ''; const ye = y === null || y === undefined || y === '';
      if (xe || ye) return xe === ye ? 0 : xe ? 1 : -1;
      return (typeof x === 'string' ? x.localeCompare(y, 'ja') : x - y) * dir;
    });
  }, [filtered, sort, starTop]);

  const onSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: SORT_START[key] || 1 }));
  const th = (key, label, cls = '') => (
    <th className={`sl-sort ${cls}`} aria-sort={sort.key === key ? (sort.dir < 0 ? 'descending' : 'ascending') : 'none'}
        onClick={() => onSort(key)}>
      {label}{sort.key === key ? (sort.dir < 0 ? ' ▼' : ' ▲') : ''}
    </th>
  );

  const reset = () => { setQ(''); setIndustry(''); setCompany(''); setPref(''); setStaff(''); setRenew(''); setCritOnly(false); setStarOnly(false); };
  const select = (label, value, set, options) => (
    <label className="sd-ctl">{label}
      <select value={value} onChange={(e) => set(e.target.value)}>
        <option value="">すべて</option>
        {options.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
      </select>
    </label>
  );

  return (
    <div className="sd">
      <div className="sd-card sd-mb">
        <header><h2>アクション率の分布</h2><span className="sd-sub">直近{days}日 ・ 濃い棒が絞り込み中の店舗 ・ 赤線より左が全店舗の下位25%</span>
          <div className="sd-tools"><a className="sd-btn sd-small" href={urls.csv}>⤓ CSV（全店舗）</a></div>
        </header>
        <div className="sd-body"><RateHistogram rows={rows} filtered={filtered} /></div>
      </div>

      <div className="sd-card">
        <div className="sl-filters">
          <label className="sd-ctl">店舗検索
            <input type="text" className="sd-wide" value={q} onChange={(e) => setQ(e.target.value)} placeholder="店舗名・Aコード・契約会社" />
          </label>
          {select('業種', industry, setIndustry, choices.industries.map((v) => [v, v]))}
          {select('契約会社', company, setCompany, choices.companies.map((c) => [String(c.id), `${c.name}（${c.n}店）`]))}
          {select('エリア', pref, setPref, choices.prefs.map((v) => [v, v]))}
          {select('担当者', staff, setStaff, choices.staffs.map((v) => [v, v]))}
          <label className="sd-ctl">契約更新
            <select value={renew} onChange={(e) => setRenew(e.target.value)}>
              {RENEW_OPTIONS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          </label>
          <label className="sc-check"><input type="checkbox" checked={critOnly} onChange={(e) => setCritOnly(e.target.checked)} /> 要対応のみ</label>
          <label className="sc-check"><input type="checkbox" checked={starOnly} onChange={(e) => setStarOnly(e.target.checked)} /> ★のみ</label>
          <label className="sc-check"><input type="checkbox" checked={starTop} onChange={(e) => setStarTop(e.target.checked)} /> ★を上に表示</label>
          <button type="button" className="sd-btn sd-small sd-ghost" onClick={reset}>条件をクリア</button>
          <span className="sd-note sl-count">{filtered.length}店 ／ 全{rows.length}店（★ {rows.filter((r) => r.star).length}店）</span>
        </div>

        <div className="sl-tbl-wrap">
          <table className="sd-table sl-table">
            <thead>
              <tr>
                <th className="sl-star-col" title="★はトラブル中・要対応必須などの目印です">★</th>
                {th('score', '要対応スコア')}
                <th>状態</th>
                {th('name', '店舗名 ／ Aコード・契約会社')}
                {th('industry', '業種')}
                {th('pref', 'エリア')}
                {th('staff', '担当者')}
                {th('kw1', 'KW1の順位', 'sd-num')}
                {th('avg', '平均順位', 'sd-num')}
                {th('uu', `UU数（${days}日）`, 'sd-num')}
                {th('rate', 'アクション率', 'sd-num')}
                {th('since', '最終対策')}
                {th('renewDays', '契約更新')}
                <th />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id} className={`sd-row-click${r.star ? ' sl-starred' : ''}`} onClick={() => { window.location.href = `${urls.dashboard}${r.id}`; }}>
                  <td className="sl-star-col"><StarButton star={r.star} onClick={() => setStarOf(r)} /></td>
                  <td><Score row={r} labels={scoreLabels} /></td>
                  <td><span className="sl-pill" style={{ '--c': STATUS[r.status].color }}>{STATUS[r.status].label}</span></td>
                  <td><b>{r.name}</b><span className="sd-sub sl-block">{[r.code, r.company].filter(Boolean).join(' ・ ')}</span></td>
                  <td>{r.industry || '—'}</td>
                  <td>{r.pref || '—'}</td>
                  <td>{r.staff || '—'}</td>
                  <td className="sd-num">{rankText(r.kw1, outOfRank)}<span className="sd-sub sl-block"><DeltaRank value={r.kw1Diff} /></span></td>
                  <td className="sd-num">{rankText(r.avg, outOfRank)}<span className="sd-sub sl-block"><DeltaRank value={r.avgDiff} /></span></td>
                  <td className="sd-num"><span className="sl-spark-cell"><Spark values={r.spark} />{fmtN(r.uu)}</span><span className="sd-sub sl-block"><DeltaPct value={r.uuDiff} /></span></td>
                  <td className="sd-num">{isNum(r.rate) ? `${fx(r.rate, 2)}%` : '—'}<span className="sd-sub sl-block"><DeltaPct value={r.rateDiff} unit="pt" /></span></td>
                  <td>{r.last ? ymd(r.last) : '—'}<span className="sd-sub sl-block">{isNum(r.since) ? `${r.since}日前` : 'なし'}</span></td>
                  <td>{r.renew ? ymd(r.renew) : '—'}<span className="sd-sub sl-block">{isNum(r.renewDays) ? (r.renewDays >= 0 ? `あと${r.renewDays}日` : `${-r.renewDays}日前`) : ''}</span></td>
                  <td>
                    <a className="sd-btn sd-small" href={`${urls.dashboard}${r.id}/compare`} onClick={(e) => e.stopPropagation()}>比較</a>
                  </td>
                </tr>
              ))}
              {!sorted.length && (
                <tr><td colSpan="14" className="sd-note">条件に合う店舗がありません。絞り込みを外すか、店舗名・Aコードを確かめてください。</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {starOf && (
        <StarDialog key={starOf.id} store={starOf} star={starOf.star} kinds={starKinds} url={`${urls.star}${starOf.id}/star`}
                    onClose={() => setStarOf(null)} onSaved={(star) => onStarSaved(starOf.id, star)} />
      )}
      {toast && <div className="sd-toast" role="status">{toast}</div>}
    </div>
  );
}

// 要対応スコア。棒の長さが点数。カーソルを乗せると内訳（項目ごとの 0〜100）が出る。
function Score({ row, labels }) {
  const tip = [`要対応スコア ${row.score}`, ...Object.entries(labels).map(([k, label]) => `${label}　${Math.round((row.parts[k] || 0) * 100)}`),
    '（全店舗の中での位置。大きいほど悪い）'].join('\n');
  return (
    <span className="sl-score" title={tip}>
      <b>{row.score}</b>
      <span className="sl-bar"><i style={{ width: `${row.score}%`, background: STATUS[row.status].color }} /></span>
    </span>
  );
}

// UU数の週ごとの合計（13週）
function Spark({ values }) {
  const W = 72; const H = 20;
  const nums = values.filter(isNum);
  if (nums.length < 2) return <svg width={W} height={H} />;
  const max = Math.max(...nums); const min = Math.min(...nums);
  const X = (i) => (i / (values.length - 1)) * (W - 2) + 1;
  const Y = (v) => (max === min ? H / 2 : H - 2 - ((v - min) / (max - min)) * (H - 4));
  let p = ''; let pen = false;
  values.forEach((v, i) => {
    if (!isNum(v)) { pen = false; return; }
    p += `${pen ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(v).toFixed(1)}`;
    pen = true;
  });
  return <svg width={W} height={H} className="sl-spark" aria-hidden="true"><path d={p} fill="none" stroke="#2a78d6" strokeWidth="1.5" /></svg>;
}

// アクション率の分布。全店舗を薄い棒、絞り込み中の店舗を濃い棒で重ね、全店舗の下位25%の境に赤線。
function RateHistogram({ rows, filtered }) {
  // 幅は枠に合わせて測る（viewBox を固定すると、枠が広いとき文字まで拡大されてしまう）
  const ref = useRef(null);
  const [W, setW] = useState(1000);
  useLayoutEffect(() => {
    const measure = () => { const el = ref.current; if (el && el.clientWidth) setW(Math.max(480, Math.round(el.clientWidth))); };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const all = rows.map((r) => r.rate).filter(isNum).sort((a, b) => a - b);
  if (!all.length) return <p className="sd-note" ref={ref}>アクション率を出せる店舗がありません。</p>;
  // 1%刻み。上位のはずれ値で横軸が伸びないよう、95%点までを棒にして、それより上は最後の棒にまとめる
  const hi = Math.max(1, Math.ceil(all[Math.floor((all.length - 1) * 0.95)]));
  const binOf = (v) => Math.min(hi, Math.floor(v));
  const bins = Array(hi + 1).fill(0); const fb = Array(hi + 1).fill(0);
  all.forEach((v) => { bins[binOf(v)] += 1; });
  filtered.forEach((r) => { if (isNum(r.rate)) fb[binOf(r.rate)] += 1; });
  const p25 = all[Math.floor((all.length - 1) * 0.25)];

  const H = 170; const ml = 34; const mr = 12; const mt = 22; const mb = 24;
  const bw = (W - ml - mr) / (hi + 1);
  const ym = niceMax(Math.max(...bins));
  const Y = (v) => mt + (H - mt - mb) * (1 - v / ym);
  const xp = ml + p25 * bw;
  const every = Math.max(1, Math.ceil((hi + 1) / 20));
  return (
    <div ref={ref}>
    <svg className="sd-chart" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="アクション率の分布">
      {[0, ym / 2, ym].map((v) => (
        <g key={v}>
          <line className="sd-grid" x1={ml} x2={W - mr} y1={Y(v)} y2={Y(v)} />
          <text x={ml - 6} y={Y(v) + 4} textAnchor="end">{Number.isInteger(v) ? v : fx(v, 1)}</text>
        </g>
      ))}
      {bins.map((c, i) => {
        const x = ml + i * bw + 1;
        const range = i === hi ? `${i}%以上` : `${i}〜${i + 1}%`;
        return (
          <g key={i}>
            {c > 0 && <rect x={x} y={Y(c)} width={Math.max(bw - 2, 1)} height={Y(0) - Y(c)} rx="3" fill="#c5dcf5" />}
            {fb[i] > 0 && <rect x={x} y={Y(fb[i])} width={Math.max(bw - 2, 1)} height={Y(0) - Y(fb[i])} rx="3" fill="#2a78d6" />}
            <rect x={x} y={mt} width={Math.max(bw - 2, 1)} height={H - mt - mb} fill="transparent">
              <title>{`アクション率 ${range}\n全店舗 ${c}店 ／ 絞り込み中 ${fb[i]}店`}</title>
            </rect>
            {i % every === 0 && <text x={ml + i * bw + bw / 2} y={H - 6} textAnchor="middle">{i === hi ? `${i}%〜` : `${i}%`}</text>}
          </g>
        );
      })}
      <line x1={xp} x2={xp} y1={mt - 8} y2={Y(0)} stroke="#e03131" strokeDasharray="4 3" strokeWidth="1.5" />
      <text x={xp + 6} y={mt - 4} style={{ fill: '#c92a2a', fontSize: 12 }}>下位25%の境界 {fx(p25, 2)}%</text>
    </svg>
    </div>
  );
}
